/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { createSlice, nanoid } from "@reduxjs/toolkit";
import { SESSION_STORAGE_DIRECT_ORDER } from "../../constants/constants";
import { cartRegistry } from "../../utils/cartRegistry";

const hydrateLineFromSession = () => {
  try {
    const stored = sessionStorage.getItem(SESSION_STORAGE_DIRECT_ORDER);
    if (!stored) return null;

    const parsed = JSON.parse(stored);

    return parsed?.line ?? null;
  } catch {
    return null;
  }
};

const persistLineToSession = (line) => {
  if (!line) {
    sessionStorage.removeItem(SESSION_STORAGE_DIRECT_ORDER);

    return;
  }

  sessionStorage.setItem(SESSION_STORAGE_DIRECT_ORDER, JSON.stringify({ line }));
};

const initialState = {
  line: hydrateLineFromSession(),
};

// A single line (a membership) that checks out alone, bypassing the cart.
const directOrderSlice = createSlice({
  name: "directOrder",
  initialState,
  reducers: {
    setDirectOrder: {
      reducer: (state, action) => {
        state.line = action.payload;
        persistLineToSession(state.line);
      },
      prepare: ({ type, entity, snapshot, params }) => {
        const built = snapshot ?? cartRegistry[type]?.buildSnapshot?.(entity) ?? null;

        return { payload: { clientId: nanoid(), type, snapshot: built, params } };
      },
    },
    repriceDirectOrder: (state, action) => {
      const { price } = action.payload;
      if (!state.line?.snapshot || state.line.snapshot.price === price) return;
      state.line = { ...state.line, snapshot: { ...state.line.snapshot, price } };
      persistLineToSession(state.line);
    },
    // Post-checkout: not a callback deviation, the callback must survive into Checkout.
    clearDirectOrder: (state) => {
      state.line = null;
      persistLineToSession(null);
    },
    // User abandonment: is a callback deviation.
    cancelDirectOrder: (state) => {
      state.line = null;
      persistLineToSession(null);
    },
  },
});

export const {
  setDirectOrder,
  repriceDirectOrder,
  clearDirectOrder,
  cancelDirectOrder,
} = directOrderSlice.actions;

export default directOrderSlice.reducer;
